import {
  ArrowLeft,
  ArrowRight,
  ListImage,
  ListNoImage,
} from '@doda/io-react/common';
import React, { ComponentProps } from 'react';
import { Select } from '@doda/io-react/pc';
import classnames from 'classnames';
import { isCanonical } from '@doda/common/src/utils/seoUtil';
import styles from './index.module.scss';
import { IconButton } from '../IconButton';
import { useCreatePageNav } from './hooks/useCreatePageNav';
import { useCreateSort } from './hooks/useCreateSort';
import { useCreateBaseUrl } from './hooks/useCreateBaseUrl';
import { useCreatePic } from './hooks/useCreatePic';
import { DEFAULT_ORDER_OPTIONS } from './constants';

/**
 * ページ遷移やデータの並べ替え・画像ありなしのオプションのProps
 */
interface Props {
  /** 次のページが存在するかどうか */
  hasNextPage: boolean;
  /** PRSRT（並べ替え）オプションが有効かどうか */
  isPrsrt?: boolean;
  /** 並べ替えの選択肢 */
  orderOptions?: ComponentProps<typeof Select>['options'];
  /** 画像ありオプションの追加パラメータ */
  picAdditionalParam?: Record<string, string>;
  /** 画像なしオプションの追加パラメータ */
  noPicAdditionalParam?: Record<string, string>;
  /** 並べ替え時の追加パラメータ */
  orderAdditionalParam?: Record<string, string>;
  /** ワイド並べ替えメニューが有効かどうか */
  isWideSortMenu?: boolean;
}

/**
 * `ページ遷移やデータの並べ替え・画像ありなしのオプションを提供。
 * @param {boolean} hasNextPage 次のページへの遷移が可能かどうかを示すフラグ
 * @param {boolean} [isPrsrt=false] 勤務地の都道府県の設定が少ない順の並び替え判定に使用されるフラグ。デフォルトはfalse。
 * @param {Array} [orderOptions=DEFAULT_ORDER_OPTIONS] 並び替えオプションの配列。デフォルトはグローバル定数 `DEFAULT_ORDER_OPTIONS` を使用。
 * @param {Record<string, string>} [picAdditionalParam] 画像有り遷移時に付与される追加のクエリパラメータ。解析系などに使用。
 * @param {Record<string, string>} [noPicAdditionalParam] 画像無し遷移時に付与される追加のクエリパラメータ。解析系などに使用。
 * @param {Record<string, string>} [orderAdditionalParam] 並び替え遷移時に付与される追加のクエリパラメータ。解析系などに使用。
 * @param {boolean} [isWideSortMenu] ソートメニューのワイド表示を行うかどうかのフラグ。指定がない場合は狭い表示が用いられる。
 */
export const CardNavigationPanel: React.FC<Props> = ({
  hasNextPage,
  isPrsrt = false,
  orderOptions = DEFAULT_ORDER_OPTIONS,
  picAdditionalParam,
  noPicAdditionalParam,
  orderAdditionalParam,
  isWideSortMenu,
}) => {
  const baseUrl = useCreateBaseUrl();
  const isStaticUrl = isCanonical(baseUrl);

  const {
    onChange,
    orderOptions: options,
    hasSuffix,
    defaultValue,
  } = useCreateSort({
    options: orderOptions,
    baseUrl,
    orderAdditionalParam,
    isStaticUrl,
  });

  const { widthImageUrl, withoutImageUrl, onClickPic } = useCreatePic({
    baseUrl,
    picAdditionalParam,
    noPicAdditionalParam,
    isStaticUrl,
  });

  const {
    previousPageHref,
    nextPageHref,
    onClickPrePageNav,
    onClickNextPageNav,
  } = useCreatePageNav({
    baseUrl,
    hasNextPage,
    isPrsrt,
    isStaticUrl,
  });

  return (
    <div className={styles.cardNavigationPanel__wrapper}>
      <div className={styles.cardNavigationPanel__selectWrapper}>
        <Select
          options={options}
          onChange={onChange}
          selectedOption={orderOptions.find(
            ({ value }) => value === defaultValue
          )}
          className={classnames(styles.cardNavigationPanel__select, {
            [styles['cardNavigationPanel__select--wide']]: isWideSortMenu,
          })}
        />
        {hasSuffix && (
          <span className={styles.cardNavigationPanel__selectSuffix}>
            で並び替え
          </span>
        )}
      </div>
      <div className={styles.cardNavigationPanel__displayFormat}>
        <IconButton
          Icon={ListImage}
          href={widthImageUrl}
          onClick={onClickPic}
        />
        <IconButton
          Icon={ListNoImage}
          href={withoutImageUrl}
          onClick={onClickPic}
        />
      </div>
      <div className={styles.cardNavigationPanel__pageNav}>
        <IconButton
          Icon={ArrowLeft}
          href={previousPageHref}
          onClick={onClickPrePageNav}
        />
        <IconButton
          Icon={ArrowRight}
          href={nextPageHref}
          onClick={onClickNextPageNav}
        />
      </div>
    </div>
  );
};
